import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Coffee } from "lucide-react";
import useSubscription from "@/hooks/useSubscription";

const DONATE_URL = process.env.REACT_APP_DONATE_URL || "https://ko-fi.com/earthpulse";

export default function SupportCreator() {
  const { active: subActive } = useSubscription();
  return (
    <section
      id="support"
      data-testid="support-creator"
      className="relative border-t border-[color:var(--gold)]/10"
      style={{ background: "linear-gradient(180deg, transparent, rgba(229, 168, 124, 0.05))" }}
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2">
            <Heart size={14} className="text-[color:var(--rose-gold)]" />
            <span className="label-overline text-[color:var(--rose-gold)]">A note from the maker</span>
          </div>

          <h2 className="mt-4 font-serif-display text-[color:var(--parchment)] text-3xl md:text-5xl leading-[1.05] tracking-tight">
            Made for you, <span className="font-serif-italic text-[color:var(--gold)]">out of love</span> — kept alive by kindness.
          </h2>

          <div className="mt-6 space-y-5 text-[color:var(--parchment-soft)] text-base md:text-lg font-sans-body leading-relaxed">
            <p>
              Everything on Earth Pulse — the live Schumann-resonance feed, the daily briefings written by AI,
              the moon-phase panels, the biorhythm calculations, the meditation library, your journal —
              costs money and time to build and keep online.
            </p>
            <p>
              I've invested my own savings so that anyone who feels the weight of these dark times for humanity
              can have a warm, honest, free place to check in with the field and with themselves.
              <span className="text-[color:var(--gold)]"> No login. No profiling. No selling of data.</span>
            </p>
            <p>
              If Earth Pulse has ever helped you land back inside your own body on a hard day —
              <em className="font-serif-italic text-[color:var(--parchment)] not-italic">
                {" "}an £8.88/month membership, or a small one-off donation, is what keeps this here for the next person.
              </em>
            </p>
          </div>

          <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3 md:gap-4">
            {subActive ? (
              <Link
                to="/subscription"
                data-testid="support-manage-sub"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
              >
                <Sparkles size={14} /> You're a member · manage
              </Link>
            ) : (
              <Link
                to="/subscribe"
                data-testid="support-subscribe"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
              >
                <Sparkles size={14} /> Unlimited readings · £8.88 / month
              </Link>
            )}
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-donate"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 font-medium"
            >
              <Coffee size={14} /> Donate any amount
            </a>
          </div>

          <p className="mt-6 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            Thank you for being here. Every contribution — big, small, or just kind words — helps.
          </p>
        </div>
      </div>
    </section>
  );
}
